import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { authService } from '../services/authService';
import { subscriptionService } from '../services/subscriptionService';
import api from '../services/api';
import { getUser, isPublicRoute } from '../utils/authUtils'; // Добавлен импорт isPublicRoute
import { match } from 'path-to-regexp';
import config from '../config';

const initialState = {
  user: getUser(),
  isAuthenticated: false,
  loading: true,
  error: null,
  authChecked: false,
};

const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING',
  CLEAR_ERROR: 'CLEAR_ERROR',
  UPDATE_PROFILE: 'UPDATE_PROFILE',
};

const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return { ...state, loading: true, error: null };
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload,
        loading: false,
        error: null,
        authChecked: true,
      };
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        loading: false,
        error: action.payload,
      };
    case AUTH_ACTIONS.LOGOUT:
      return { ...state, isAuthenticated: false, user: null, loading: false, authChecked: true, };
    case AUTH_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    case AUTH_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };
    case AUTH_ACTIONS.UPDATE_PROFILE:
      return { ...state, user: { ...state.user, ...action.payload } };
    default:
      return state;
  }
};

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const location = useLocation();
  
  useEffect(() => {
    const fetchInitialAuth = async () => {
      // Если это публичная страница и пользователь не залогинен - не делаем запрос
      if (isPublicRoute(location.pathname) && !state.user) {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        return;
      }

      if (state.isAuthenticated) {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      }

      try {
        const response = await api.get('/auth/me/');
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: response.data.user,
        });
      } catch (err) {
        // Не делаем logout на публичных страницах
        if (isPublicRoute(location.pathname)) {
          dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        } else {
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
      }
    };

    fetchInitialAuth();
  }, [location.pathname]);

  const login = async (credentials) => {
    const result = await authService.login(credentials);
    if (result.success) {
      dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: result.data.user });
    }
    return result;
  };

  const register = async (credentials) => {
    return await authService.register(credentials);
  };

  const logout = async () => {
    await authService.logout();
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  };

  const updateProfile = async (profileData) => {
    const result = await authService.updateProfile(profileData);
    if (result.success) {
      dispatch({ type: AUTH_ACTIONS.UPDATE_PROFILE, payload: result.user });
    }
    return result;
  };
  
  const updateUser = (user) => {
    dispatch({ type: AUTH_ACTIONS.UPDATE_PROFILE, payload: user });
  }

  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  const requestPasswordReset = async (email) => {
    return await authService.passwordResetRequest(email);
  };

  const confirmPasswordReset = async (data) => {
    const result = await authService.passwordResetConfirm(data);
    if (result.success) {
      dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: result.data.user });
    }
    return result;
  };

  const resendVerification = async (email) => {
    return await authService.resendVerification(email);
  };

  const verifyEmail = async (uidb64, token) => {
    const result = await authService.verifyEmail(uidb64, token);
    if (result.success) {
      dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: result.data.user });
    }
    return result;
  };
  
  const loginGoogle = async (decoded) => {
    const result = await authService.googleAuth(decoded);
    if (result.success) {
      dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: result.data.user });
    }
    return result;
  };
  
  const isSubscribed = async () => {
    try {
      const { status } = await subscriptionService.getSubscriptionStatus();
      return status === 'active';
    } catch (err) {
      return false;
    }
  };

  const value = {
    ...state,
    login,
    register,
    logout,
    loginGoogle,
    updateProfile,
    clearError,
    requestPasswordReset,
    confirmPasswordReset,
    resendVerification,
    verifyEmail,
    isSubscribed,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      { children }
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;